/**
 * HealthScorePane - Visual health metrics and progress indicators
 * Shows overall PMF health and per-phase scores
 */

import { useEffect } from 'react'
import { useJourneyStore } from '@/stores/journeyStore'
import { useSessionStore } from '@/stores/sessionStore'
import { Progress } from '@/components/ui/progress'
import { TrendingUp, AlertCircle, CheckCircle, Clock } from 'lucide-react'

export function HealthScorePane() {
  const { accountId, projectId } = useSessionStore()
  const {
    fetchJourney,
    getOverallProgress,
    getPhaseProgress,
    getActivePhases,
  } = useJourneyStore()

  // Fetch journey data on mount
  useEffect(() => {
    if (accountId && projectId) {
      fetchJourney(accountId, projectId)
    }
  }, [accountId, projectId, fetchJourney])

  const overallHealth = getOverallProgress()
  const phases = getActivePhases()

  // Build phase progress map
  const phaseProgressMap: Record<string, number> = {}
  phases.forEach(phase => {
    phaseProgressMap[phase.id] = getPhaseProgress(phase.id)
  })

  const progress = {
    byPhase: phaseProgressMap,
    overall: overallHealth,
    required: { completed: 0, total: 0 },
  }

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      {/* Overall Health Score */}
      <div className="text-center py-4">
        <div className="relative inline-flex animate-in zoom-in duration-500">
          <svg className="w-32 h-32" viewBox="0 0 100 100">
            {/* Background circle */}
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="var(--color-warm-200)"
              strokeWidth="8"
            />
            {/* Progress circle */}
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke={getHealthColor(overallHealth)}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${overallHealth * 2.51} 251`}
              transform="rotate(-90 50 50)"
              style={{
                transition: 'stroke-dasharray 0.6s ease-in-out, stroke 0.3s ease-in-out',
              }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-3xl font-bold text-warm-900 transition-all duration-500">
                {Math.round(overallHealth)}%
              </div>
              <div className="text-xs text-warm-600">Health</div>
            </div>
          </div>
        </div>
        <div className="mt-2 text-sm text-warm-700">
          Overall Product Health
        </div>
      </div>

      {/* Entity Gaps (if any) */}
      {progress.required.total > progress.required.completed && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-start gap-2">
            <AlertCircle size={14} className="text-amber-600 mt-0.5" />
            <div className="flex-1 text-xs">
              <div className="font-medium text-amber-900 mb-1">Missing Required Items</div>
              <div className="text-amber-700">
                {progress.required.total - progress.required.completed} of {progress.required.total} required activities incomplete
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phase Health Breakdown */}
      {phases.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-warm-900">
            <TrendingUp size={14} style={{ color: 'var(--color-warm-600)' }} />
            <span>Phase Progress</span>
          </div>

          {phases.map((phase, index) => {
            const phaseProgress = getPhaseProgress(phase.id)
            const isComplete = phaseProgress >= 90
            return (
              <div
                key={phase.id}
                className="animate-in slide-in-from-left duration-300"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <HealthBar
                  label={phase.name}
                  value={phaseProgress}
                  color={phase.color}
                  isComplete={isComplete}
                />
              </div>
            )
          })}
        </div>
      )}

    </div>
  )
}

function HealthBar({
  label,
  value,
  color,
  isComplete,
}: {
  label: string
  value: number
  color: string
  isComplete?: boolean
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center text-xs">
        <div className="flex items-center gap-1.5">
          {isComplete ? (
            <CheckCircle size={12} className="text-green-600" />
          ) : value > 0 ? (
            <Clock size={12} className="text-warm-500" />
          ) : null}
          <span className="text-warm-700">{label}</span>
        </div>
        <span className="font-medium text-warm-900">{Math.round(value)}%</span>
      </div>
      <Progress
        value={value}
        className="h-2"
        style={{
          backgroundColor: `${color}20`,
        }}
        indicatorClassName="bg-transparent"
        indicatorStyle={{
          backgroundColor: color,
        }}
      />
    </div>
  )
}

function getHealthColor(health: number): string {
  if (health >= 91) return 'var(--color-business)' // Excellent - Blue
  if (health >= 71) return 'var(--color-success)' // Strong - Green
  if (health >= 51) return 'var(--color-solution)' // Progress - Yellow
  if (health >= 31) return '#D4A574' // Building - Orange
  return 'var(--color-error)' // Critical - Red
}
